import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AuthContext } from "../../context/AuthContext";
import { editProfileDetails } from "../../services/userService";
import axiosInstance from "../../services/axiosInstance";
import { resolveAssetUrl } from "../../services/apiConfig";
import Button from "../Button/Button";
import ImagePicker from "../imagePicker/ImagePicker";
import styles from "./ProfileEdit.module.css";

function EditUserDetails({ isVisible, handleModal, username, onBusyChange }) {
  const navigate = useNavigate();
  const { user, setAuthUser } = useContext(AuthContext);
  const [image, setImage] = useState(null);
  useEffect(() => () => { if (image) URL.revokeObjectURL(image); }, [image]);

  const formik = useFormik({
    initialValues: { username: user?.username || username || "", bio: user?.bio || "", mobile: user?.contact_number || "", image: null },
    validationSchema: Yup.object({
      username: Yup.string().min(3, "Use pelo menos 3 caracteres").max(30, "Use no máximo 30 caracteres").required("Informe seu nome de usuário"),
      bio: Yup.string().max(30, "A biografia deve ter no máximo 30 caracteres"),
      mobile: Yup.string().test("phone", "Informe um telefone com DDD, com 10 ou 11 dígitos", (value) => !value || /^\d{10,11}$/.test(value.replace(/\D/g, ""))),
      image: Yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      onBusyChange?.(true);
      try {
        const response = await editProfileDetails({ ...values, username: values.username.trim(), mobile: values.mobile.replace(/\D/g, "") });
        if (!response.success) { toast.error(response.message || "Não foi possível atualizar o perfil."); return; }
        try {
          const updated = await axiosInstance.get("/auth/verify_token");
          setAuthUser(updated.data.user);
        } catch {
          setAuthUser({ ...user, username: values.username.trim(), bio: values.bio.trim() });
        }
        toast.success("Perfil atualizado com sucesso!");
        handleModal(false);
        navigate("/profile/" + encodeURIComponent(values.username.trim()), { replace: true });
      } catch {
        toast.error("Não foi possível atualizar o perfil. Tente novamente.");
      } finally {
        onBusyChange?.(false);
      }
    },
  });

  if (!isVisible) return null;
  const error = (name) => formik.touched[name] && formik.errors[name];
  const handleImageChange = (file, normalized) => {
    if (!file) return;
    setImage(normalized.previewUrl);
    formik.setFieldValue("image", file);
  };

  return <form className={styles.form} onSubmit={formik.handleSubmit}>
    <div className={styles.photoSection}>
      <img className={styles.avatar} src={image || resolveAssetUrl(user?.profile_url || "images/profile_image/user.png")} alt="Foto do perfil" />
      <div><h3>Foto do perfil</h3><p>Escolha uma foto que combine com você.</p><ImagePicker label={image ? "Trocar foto" : "Alterar foto"} onSelect={handleImageChange} capture="user" maxSize={5 * 1024 * 1024} disabled={formik.isSubmitting} onError={toast.error} /></div>
    </div>
    <div className={styles.fields}>
      <label htmlFor="profile-username">Nome de usuário
        <input id="profile-username" name="username" autoComplete="username" maxLength={30} value={formik.values.username} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={formik.isSubmitting} aria-invalid={Boolean(error("username"))} aria-describedby={error("username") ? "profile-username-error" : undefined} />
        {error("username") && <span id="profile-username-error" className={styles.errorMessage}>{error("username")}</span>}
      </label>
      <label htmlFor="profile-mobile">Celular
        <input id="profile-mobile" name="mobile" type="tel" autoComplete="tel" placeholder="DDD e número" value={formik.values.mobile} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={formik.isSubmitting} aria-invalid={Boolean(error("mobile"))} aria-describedby={error("mobile") ? "profile-mobile-error" : undefined} />
        {error("mobile") && <span id="profile-mobile-error" className={styles.errorMessage}>{error("mobile")}</span>}
      </label>
      <label className={styles.fullField} htmlFor="profile-bio">Biografia
        <textarea id="profile-bio" name="bio" rows={3} maxLength={30} placeholder="Conte um pouco sobre você" value={formik.values.bio} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={formik.isSubmitting} aria-invalid={Boolean(error("bio"))} aria-describedby="profile-bio-help" />
        <span id="profile-bio-help" className={styles.fieldHint}>{error("bio") || (formik.values.bio.length + "/30 caracteres")}</span>
      </label>
    </div>
    <footer className={styles.actions}>
      <Button type="button" variant="secondary" disabled={formik.isSubmitting} onClick={() => handleModal(false)}>Cancelar</Button>
      <Button type="submit" disabled={formik.isSubmitting}>{formik.isSubmitting ? "Salvando..." : "Salvar alterações"}</Button>
    </footer>
  </form>;
}
export default EditUserDetails;
